import { useState } from 'react';
import { ListBulleted, Tag as TagIcon, Search } from '@carbon/icons-react';
import { ClaimsFacet } from './ClaimsFacet';
import { EntitiesFacet } from './EntitiesFacet';
import { SemanticFacet } from './SemanticFacet';
import './FacetSearch.scss';

export type FacetTab = 'claims' | 'entities' | 'semantic';

const TABS: Array<{ value: FacetTab; label: string; icon: typeof ListBulleted; title: string }> = [
  { value: 'claims', label: 'Claims', icon: ListBulleted, title: 'Search triplets (subject–predicate–object) extracted by L3' },
  { value: 'entities', label: 'Entities', icon: TagIcon, title: 'Search named entities extracted by L3' },
  { value: 'semantic', label: 'Semantic', icon: Search, title: 'Ask a question — vector similarity search over KU embeddings' },
];

/**
 * FacetSearchPanel — the KB-scoped facet tabs (Claims / Entities / Semantic),
 * without the KB picker. Reused by the standalone FacetSearchPage (with a KB
 * selector) and by the Knowledge Explorer, where the KB is fixed by the open
 * exploration so facet search is available in-context (Feedback A11).
 */
export const FacetSearchPanel = ({ kbId }: { kbId: string }) => {
  const [activeTab, setActiveTab] = useState<FacetTab>('claims');

  return (
    <div className="facet-search__panel" data-testid="facet-search-panel">
      <div className="facet-search__tabs">
        {TABS.map(({ value, label, icon: Icon, title }) => (
          <button
            key={value}
            className={`facet-search__tab${activeTab === value ? ' facet-search__tab--active' : ''}`}
            title={title}
            onClick={() => setActiveTab(value)}
            data-testid={`facet-tab-${value}`}
          >
            <Icon size={14} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'claims' && <ClaimsFacet kbId={kbId} />}
      {activeTab === 'entities' && <EntitiesFacet kbId={kbId} />}
      {activeTab === 'semantic' && <SemanticFacet kbId={kbId} />}
    </div>
  );
};
